// To generate a new file similar to this one, run `pay js:run gql-v1 --type=query --name=<name> --dir=path/to/dir`.
// You can freely edit this file. To update the generated types, run `eslint` in `--fix` mode.
// Do not delete this comment. https://go/gql-operation-definition

import {useEffect} from 'react';
import type {GraphQlDocument, UseQueryOptions} from '@sail/data';

import {useQuery} from '@sail/data';
import {useReports} from '@sail/observability';
import {getOperationName, isEmptyObjectToken} from './useV2QueryUtils';

import type {V2QueryOptions, V2QueryReturn} from '../types';
import type {StripeContextInput} from '../../../schema/types';
import getApiKeyFromToken from '../../shared/permissions/getApiKeyFromToken';
import getIdFromToken from '../../shared/permissions/getIdFromToken';
import getLiveModeFromToken from '../../shared/permissions/getLiveModeFromToken';
import type {V2InternalPermissionToken} from '../../shared/permissions/types';

const loggedEmptyTokenReportKeys = new Set<string>();

function getEmptyTokenReportKey(operationName: string, component?: string) {
  return `${operationName}:${component ?? ''}`;
}

export default function useV2Query<
  TData,
  TVariables extends {v2Context: StripeContextInput},
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  TPermissions extends V2InternalPermissionToken<any, any>,
  TPermissionsOfOptions extends TPermissions,
>(
  query: GraphQlDocument<TData, TVariables, TPermissions>,
  options: V2QueryOptions<
    GraphQlDocument<TData, TVariables, TPermissionsOfOptions>
  >,
): V2QueryReturn<GraphQlDocument<TData, TVariables, TPermissions>> {
  const {warning} = useReports();
  const operationName = getOperationName(query);
  const isEmptyToken = isEmptyObjectToken(options.token);

  useEffect(() => {
    if (!isEmptyToken) {
      return;
    }

    const reportKey = getEmptyTokenReportKey(operationName, options.component);

    if (loggedEmptyTokenReportKeys.has(reportKey)) {
      return;
    }

    loggedEmptyTokenReportKeys.add(reportKey);

    warning('data-v2 query received an empty object token', {
      project: 'sail_core',
      tags: {
        operationName,
      },
      extras: {
        component: options.component,
        eventName: 'data_v2_query_empty_token',
      },
    });
  }, [isEmptyToken, operationName, options.component, warning]);

  const finalOptions = Object.assign({}, options, {
    variables: Object.assign({}, options?.variables, {
      v2Context: {
        apiKey: options.token ? getApiKeyFromToken(options.token) : undefined,
        id: options.token ? getIdFromToken(options.token) : undefined,
        liveMode: options.token
          ? getLiveModeFromToken(options.token)
          : undefined,
      },
    }),
  }) as UseQueryOptions<GraphQlDocument<TData, TVariables, TPermissions>>;

  return useQuery(query, finalOptions) as V2QueryReturn<
    GraphQlDocument<TData, TVariables, TPermissions>
  >;
}
